import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { buildTitleDeepLink } from "../../../outlets/dashboard/titleDeepLink";

import {
  createResourceGroupAiAnalysis,
  fetchAiAnalysis,
  fetchAiAccess,
  fetchResourceGroupAiHistory,
  submitAiFeedback,
} from "./aiApi";
import ResourceGroupFinancialSummary from "./ResourceGroupFinancialSummary";
import { isPresentationProject } from "../../../utils/presentationMode";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "all", label: "Todos os status" },
  { value: "overrun", label: "Estouro" },
  { value: "savings", label: "Economia" },
  { value: "balanced", label: "Equilibrado" },
  { value: "no_movement", label: "Sem movimento" },
  { value: "insufficient_evidence", label: "Evidência insuficiente" },
];

const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.filter(({ value }) => value !== "all").map(
    ({ value, label }) => [value, label],
  ),
);

const INSUFFICIENCY_LABELS = {
  wbs_allocation_unavailable: "Rateio por WBS indisponível",
  budget_unavailable: "Orçamento indisponível",
  actual_unavailable: "Apropriação indisponível",
  comparison_unavailable: "Comparação indisponível",
};

const HISTORY_STATUS_LABELS = {
  partial: "Análise concluída",
  completed: "Análise concluída",
  provider_unavailable: "IA indisponível",
  insufficient_data: "Evidência insuficiente",
  processing: "Em processamento",
  failed: "Não concluída",
};

const RETRYABLE_ANALYSIS_CODES = new Set(["resource_group_no_evidence"]);
const PROCESSING_POLL_DELAYS_MS = [750, 1500, 3000];

const currency = (cents) => {
  if (!Number.isFinite(cents)) return "Sem dado comparável";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(cents / 100)
    .replace(/\u00a0/g, " ");
};

const dateLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Data indisponível";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeZone: "America/Sao_Paulo",
  }).format(date);
};

const quotaText = (quota, period) => {
  if (!quota) return "Cota indisponível";
  if (quota.unlimited) return "Uso ilimitado";
  if (quota.remaining === 0) return "Cota utilizada neste período";
  const noun =
    quota.remaining === 1 ? "análise restante" : "análises restantes";
  return `${quota.remaining} ${noun} ${
    period === "monthly" ? "neste mês" : "nesta semana"
  }`;
};

const normalizedText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");

const driverImpact = (driver) =>
  Number.isFinite(driver?.totalVarianceCents)
    ? Math.abs(driver.totalVarianceCents)
    : 0;

const causalValues = ({
  priceEffectCents,
  priceEffectAvailable,
  quantityEffectCents,
  quantityEffectAvailable,
  unexplainedEffectCents,
  effectAvailability,
}) => {
  const priceAvailability =
    effectAvailability?.price ??
    (priceEffectAvailable === false || !Number.isFinite(priceEffectCents)
      ? "unavailable"
      : "available");
  const quantityAvailability =
    effectAvailability?.quantity ??
    (quantityEffectAvailable === false || !Number.isFinite(quantityEffectCents)
      ? "unavailable"
      : "available");
  let residualAvailability = effectAvailability?.residual;
  if (!residualAvailability) {
    residualAvailability = !Number.isFinite(unexplainedEffectCents)
      ? "unavailable"
      : "partial";
    if (
      Number.isFinite(unexplainedEffectCents) &&
      priceAvailability === "available" &&
      quantityAvailability === "available"
    ) {
      residualAvailability = "available";
    }
  }
  return [
    {
      key: "price",
      label: "Preço",
      value: priceEffectCents,
      availability: priceAvailability,
    },
    {
      key: "quantity",
      label: "Quantidade",
      value: quantityEffectCents,
      availability: quantityAvailability,
    },
    {
      key: "residual",
      label: "Residual",
      value: unexplainedEffectCents,
      availability: residualAvailability,
    },
  ];
};

const causalSegmentText = (segment) => {
  if (segment.availability === "unavailable") return "Sem dado comparável";
  const value = currency(segment.value);
  return segment.availability === "partial" ? `${value} · Parcial` : value;
};

const CausalRail = ({ values, label, context = "item" }) => {
  const segments = causalValues(values);
  const complete = segments.every(
    (segment) => segment.availability === "available",
  );
  const total = segments.reduce(
    (sum, segment) =>
      segment.availability !== "unavailable" && Number.isFinite(segment.value)
        ? sum + Math.abs(segment.value)
        : sum,
    0,
  );
  const accessible = segments
    .map((segment) => `${segment.label}: ${causalSegmentText(segment)}`)
    .join(", ");
  const incomplete = segments.some(
    (segment) => segment.availability !== "available",
  );
  let emptyLabel = "Decomposição indisponível";
  if (complete) {
    emptyLabel = "Sem efeito observado";
  } else if (
    segments.some((segment) => segment.availability !== "unavailable")
  ) {
    emptyLabel = "Sem efeito observado nos dados comparáveis";
  }

  return (
    <div
      className={`resource-group-ai-causal${
        total === 0 ? " resource-group-ai-causal--empty" : ""
      }`}
    >
      <div
        className="resource-group-ai-causal__legend"
        role="img"
        aria-label={`${label}: ${accessible}`}
      >
        {segments.map((segment) => (
          <span
            key={segment.key}
            className={`resource-group-ai-causal__label resource-group-ai-causal__label--${segment.key} resource-group-ai-causal__label--state-${segment.availability}`}
          >
            <b>{segment.label}</b>
            <small>{causalSegmentText(segment)}</small>
          </span>
        ))}
      </div>
      {total > 0 ? (
        <div className="resource-group-ai-causal__track" aria-hidden="true">
          {segments
            .filter((segment) => segment.availability !== "unavailable")
            .map((segment) => (
              <span
                key={segment.key}
                className={`resource-group-ai-causal__segment resource-group-ai-causal__segment--${segment.key}`}
                style={{
                  width: `${(Math.abs(segment.value ?? 0) / total) * 100}%`,
                }}
              />
            ))}
        </div>
      ) : (
        <small className="resource-group-ai-causal__unavailable">
          {emptyLabel}
        </small>
      )}
      {incomplete ? (
        <small className="resource-group-ai-causal__partial">
          {context === "group"
            ? "Cobertura parcial dos efeitos do grupo: há insumos sem preço ou quantidade comparável."
            : "Decomposição parcial: há efeito sem dado comparável; o residual preserva a parcela ainda não explicada."}
        </small>
      ) : null}
    </div>
  );
};

const matchLabel = (driver) => {
  if (!Array.isArray(driver?.equivalences) || driver.equivalences.length === 0)
    return null;
  if (driver.match?.status === "confirmed") return "Correspondência confirmada";
  if (driver.match?.status === "candidate")
    return "Possível equivalência — confira";
  return null;
};

const DriverMatch = ({ driver }) => {
  const label = matchLabel(driver);
  if (!label) return null;
  return (
    <div
      className={`resource-group-ai-match resource-group-ai-match--${driver.match.status}`}
    >
      <strong>{label}</strong>
      <ul>
        {driver.equivalences.map((member) => (
          <li key={`${member.side}-${member.resourceId}`}>
            {member.side === "budget" ? "Orçamento" : "Apropriação"}:{" "}
            {member.resourceId} · {member.name}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DriverTable = ({ drivers }) => (
  <div className="resource-group-ai-table-wrap">
    <table
      className="resource-group-ai-driver-table"
      aria-label="Lista completa de insumos analisados"
    >
      <thead>
        <tr>
          <th scope="col">Insumo</th>
          <th scope="col">Orçado</th>
          <th scope="col">Apropriado</th>
          <th scope="col">Desvio</th>
          <th scope="col">Trilho causal</th>
          <th scope="col">Situação</th>
        </tr>
      </thead>
      <tbody>
        {drivers.length > 0 ? (
          drivers.map((driver) => (
            <tr key={driver.candidateId}>
              <td data-label="Insumo" className="resource-group-ai-driver-name">
                <span>{driver.name}</span>
                <small>
                  {driver.resourceId} · {driver.unit || "Unidade não informada"}
                </small>
              </td>
              <td data-label="Orçado">{currency(driver.budgetTotalCents)}</td>
              <td data-label="Apropriado">
                {currency(driver.actualTotalCents)}
              </td>
              <td
                data-label="Desvio"
                className={`resource-group-ai-variance resource-group-ai-variance--${driver.status}`}
              >
                {currency(driver.totalVarianceCents)}
              </td>
              <td
                data-label="Trilho causal"
                className="resource-group-ai-causal-cell"
              >
                <CausalRail
                  values={driver}
                  label={`Composição causal do insumo ${driver.resourceId}`}
                />
              </td>
              <td
                data-label="Situação"
                className="resource-group-ai-status-cell"
              >
                <span
                  className={`resource-group-ai-status resource-group-ai-status--${driver.status}`}
                >
                  {STATUS_LABELS[driver.status] ?? "Situação indisponível"}
                </span>
                {driver.insufficiencyReason ? (
                  <small>
                    {INSUFFICIENCY_LABELS[driver.insufficiencyReason] ??
                      "Evidência não comparável"}
                  </small>
                ) : null}
                <DriverMatch driver={driver} />
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="6" className="resource-group-ai-empty">
              Nenhum insumo corresponde aos filtros.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const Narrative = ({ narrative, drivers }) => {
  if (!narrative) return null;
  const labelsByRef = new Map();
  drivers.forEach((driver) => {
    (driver.evidenceRefs ?? []).forEach((ref) => {
      labelsByRef.set(ref, `${driver.resourceId} · ${driver.name}`);
    });
  });
  return (
    <section
      className="resource-group-ai-narrative"
      aria-labelledby="resource-group-ai-narrative-title"
    >
      <span className="item-ai-eyebrow">Leitura construtiva</span>
      <h4 id="resource-group-ai-narrative-title">O que mais explica o grupo</h4>
      <p className="resource-group-ai-narrative__summary">
        {narrative.summary}
      </p>
      {narrative.keyFindings?.length > 0 ? (
        <div className="resource-group-ai-findings">
          {narrative.keyFindings.map((finding) => (
            <article
              key={`${finding.title}-${finding.evidenceRefs?.join("|")}`}
            >
              <h5>{finding.title}</h5>
              <p>{finding.explanation}</p>
              {finding.evidenceRefs?.length > 0 ? (
                <div aria-label="Insumos que sustentam esta conclusão">
                  {finding.evidenceRefs.map((ref) => (
                    <span key={ref}>
                      {labelsByRef.get(ref) ?? "Evidência estruturada do grupo"}
                    </span>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
      <div className="resource-group-ai-checks">
        <div>
          <h5>O que conferir agora</h5>
          <ul>
            {narrative.recommendedChecks?.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        {narrative.limitations?.length > 0 ? (
          <div>
            <h5>Limites desta leitura</h5>
            <ul>
              {narrative.limitations.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const FINANCIAL_DRIVER_STATUSES = new Set(["overrun", "savings", "balanced"]);

const groupEffectAvailability = (drivers, flag, value) => {
  const comparable = drivers.filter(
    (driver) =>
      FINANCIAL_DRIVER_STATUSES.has(driver.status) &&
      Number.isFinite(driver.totalVarianceCents),
  );
  if (comparable.length === 0) return "unavailable";
  const available = comparable.filter(
    (driver) => driver[flag] === true && Number.isFinite(driver[value]),
  ).length;
  if (available === 0) return "unavailable";
  return available === comparable.length ? "available" : "partial";
};

const GroupSummary = ({ output }) => {
  const { summary, coverage, drivers = [] } = output;
  const priceAvailability = groupEffectAvailability(
    drivers,
    "priceEffectAvailable",
    "priceEffectCents",
  );
  const quantityAvailability = groupEffectAvailability(
    drivers,
    "quantityEffectAvailable",
    "quantityEffectCents",
  );
  let residualAvailability = "unavailable";
  if (Number.isFinite(summary?.unexplainedEffectCents)) {
    residualAvailability =
      priceAvailability === "available" && quantityAvailability === "available"
        ? "available"
        : "partial";
  }
  const budget = summary?.budgetTotalCents;
  const variance = summary?.totalVarianceCents;
  const variancePercent =
    Number.isFinite(budget) && budget !== 0 && Number.isFinite(variance)
      ? `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(
          (variance / Math.abs(budget)) * 100,
        )}%`
      : "Percentual indisponível";
  return (
    <section
      className="resource-group-ai-ledger"
      aria-label="Resumo financeiro determinístico do grupo"
    >
      <div className="resource-group-ai-ledger__headline">
        <span>Desvio do grupo</span>
        <strong>{currency(variance)}</strong>
        <small>{variancePercent} sobre o orçamento comparável</small>
      </div>
      <dl>
        <div>
          <dt>Orçado comparável</dt>
          <dd>{currency(summary?.budgetTotalCents)}</dd>
        </div>
        <div>
          <dt>Apropriado comparável</dt>
          <dd>{currency(summary?.actualTotalCents)}</dd>
        </div>
        <div>
          <dt>Estouro</dt>
          <dd>{summary?.overrunItems ?? 0} insumos</dd>
        </div>
        <div>
          <dt>Economia</dt>
          <dd>{summary?.savingsItems ?? 0} insumos</dd>
        </div>
        <div>
          <dt>Sem movimento</dt>
          <dd>{summary?.noMovementItems ?? 0} insumos</dd>
        </div>
        <div>
          <dt>Evidência insuficiente</dt>
          <dd>{summary?.insufficientItems ?? 0} insumos</dd>
        </div>
      </dl>
      <CausalRail
        values={{
          ...(summary ?? {}),
          effectAvailability: {
            price: priceAvailability,
            quantity: quantityAvailability,
            residual: residualAvailability,
          },
        }}
        label="Composição causal do grupo"
        context="group"
      />
      <div className="resource-group-ai-coverage">
        <div>
          <strong>
            Analisados {coverage?.analyzedItems ?? 0} de{" "}
            {coverage?.totalItems ?? 0} insumos
          </strong>
          <span>
            {coverage?.insufficientItems ?? 0} com evidência insuficiente
          </span>
        </div>
        <progress
          aria-label="Cobertura dos insumos analisáveis"
          max={Math.max(coverage?.totalItems ?? 0, 1)}
          value={coverage?.analyzedItems ?? 0}
        />
        <small>
          A narrativa considera os {coverage?.providerTopItems ?? 0} maiores
          impactos; a lista abaixo mantém todos os insumos.
        </small>
      </div>
    </section>
  );
};

const Feedback = ({ workId, analysisId }) => {
  const [state, setState] = useState("idle");
  const save = async (helpful) => {
    if (state !== "idle") return;
    setState("saving");
    try {
      await submitAiFeedback(workId, analysisId, { helpful, comment: "" });
      setState("saved");
    } catch {
      setState("error");
    }
  };
  return (
    <div className="item-ai-feedback resource-group-ai-feedback">
      <span>Esta análise ajudou?</span>
      <button
        type="button"
        aria-label="Sim, esta análise ajudou"
        disabled={state !== "idle"}
        onClick={() => save(true)}
      >
        Sim
      </button>
      <button
        type="button"
        aria-label="Não, esta análise não ajudou"
        disabled={state !== "idle"}
        onClick={() => save(false)}
      >
        Não
      </button>
      {state === "saved" ? (
        <small role="status">Obrigado pelo retorno.</small>
      ) : null}
      {state === "error" ? (
        <small role="alert">Não foi possível salvar seu retorno.</small>
      ) : null}
    </div>
  );
};

const GroupResult = ({ analysis, workId, history, readOnly = false }) => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [order, setOrder] = useState("impact_desc");
  const [page, setPage] = useState(1);
  const output = analysis?.output;
  const allDrivers = Array.isArray(output?.drivers) ? output.drivers : [];
  const normalizedQuery = normalizedText(query.trim());
  const filteredDrivers = useMemo(
    () =>
      allDrivers
        .filter((driver) => status === "all" || driver.status === status)
        .filter((driver) => {
          if (!normalizedQuery) return true;
          const equivalences = (driver.equivalences ?? [])
            .map((member) => `${member.resourceId} ${member.name}`)
            .join(" ");
          return normalizedText(
            `${driver.resourceId} ${driver.name} ${driver.canonicalKey} ${equivalences}`,
          ).includes(normalizedQuery);
        })
        .sort((left, right) => {
          const impactDelta = driverImpact(left) - driverImpact(right);
          const orderedImpact =
            order === "impact_asc" ? impactDelta : -impactDelta;
          return (
            orderedImpact ||
            String(left.candidateId).localeCompare(String(right.candidateId))
          );
        }),
    [allDrivers, normalizedQuery, order, status],
  );

  if (!output) return null;
  const totalPages = Math.max(1, Math.ceil(filteredDrivers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visibleDrivers = filteredDrivers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );
  const older = history.filter((run) => run.analysisId !== analysis.analysisId);

  return (
    <section
      className="item-ai-result resource-group-ai-result"
      aria-label="Resultado da análise do grupo de insumos"
    >
      {analysis.status === "provider_unavailable" ? (
        <p className="item-ai-provider-warning" role="status">
          A IA não respondeu, mas os cálculos do grupo continuam disponíveis.
          Esta tentativa não consumiu sua cota.
        </p>
      ) : null}
      {output.contractVersion === 2 ? (
        <ResourceGroupFinancialSummary
          workId={workId}
          verdict={output.verdict}
          reconciliation={output.reconciliation}
          resourceCoverage={output.resourceCoverage}
          financialTitles={output.financialTitles}
          sourceState={output.sourceState}
          buildTitleHref={buildTitleDeepLink}
        />
      ) : null}
      <Narrative narrative={output.narrative} drivers={allDrivers} />
      {output.contractVersion === 2 ? null : <GroupSummary output={output} />}
      <section
        className="resource-group-ai-drivers"
        aria-labelledby="resource-group-ai-drivers-title"
      >
        <header>
          <div>
            <span className="item-ai-eyebrow">Lista auditável</span>
            <h4 id="resource-group-ai-drivers-title">
              Todos os insumos analisados
            </h4>
          </div>
          <small>Filtros e páginas não usam sua cota.</small>
        </header>
        <div className="resource-group-ai-filters">
          <label>
            <span>Buscar insumo na análise</span>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Código ou nome"
            />
          </label>
          <label>
            <span>Filtrar por status</span>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Ordenar insumos</span>
            <select
              value={order}
              onChange={(event) => {
                setOrder(event.target.value);
                setPage(1);
              }}
            >
              <option value="impact_desc">Maior impacto absoluto</option>
              <option value="impact_asc">Menor impacto absoluto</option>
            </select>
          </label>
        </div>
        <DriverTable drivers={visibleDrivers} />
        <nav
          className="resource-group-ai-pagination"
          aria-label="Paginação dos insumos analisados"
        >
          <button
            type="button"
            disabled={safePage === 1}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
          >
            Página anterior
          </button>
          <span>
            Página {safePage} de {totalPages} · {filteredDrivers.length} insumos
          </span>
          <button
            type="button"
            disabled={safePage === totalPages}
            onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
          >
            Próxima página
          </button>
        </nav>
      </section>
      {analysis.state === "completed" && !readOnly ? (
        <Feedback
          key={analysis.analysisId}
          workId={workId}
          analysisId={analysis.analysisId}
        />
      ) : null}
      {older.length > 0 ? (
        <section
          className="resource-group-ai-history"
          aria-labelledby="resource-group-ai-history-title"
        >
          <h4 id="resource-group-ai-history-title">Análises anteriores</h4>
          <ul>
            {older.map((run) => (
              <li key={run.analysisId}>
                <strong>{dateLabel(run.completedAt ?? run.createdAt)}</strong>
                <span>
                  {HISTORY_STATUS_LABELS[run.status] ??
                    HISTORY_STATUS_LABELS[run.state] ??
                    "Status indisponível"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
};

const ResourceGroupAiPanel = ({
  workId,
  contextId,
  groupName,
  project,
  onClose,
  preserveScrollOnFocus = false,
}) => {
  const presentationReadOnly = isPresentationProject(project);
  const contextKey = `${workId}:${contextId}:${presentationReadOnly ? "read-only" : "mutable"}`;
  const titleRef = useRef(null);
  const mountedRef = useRef(false);
  const scopeRef = useRef({ key: contextKey, generation: 0 });
  if (scopeRef.current.key !== contextKey) {
    scopeRef.current = {
      key: contextKey,
      generation: scopeRef.current.generation + 1,
    };
  }
  const loadControllerRef = useRef(null);
  const requestRef = useRef(null);
  const pollControllerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const quotaReconcileControllerRef = useRef(null);
  const idempotencyKeyRef = useRef(null);
  const [access, setAccess] = useState(null);
  const [quota, setQuota] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [errorPhase, setErrorPhase] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [replayNotice, setReplayNotice] = useState(false);
  const [pollCycle, setPollCycle] = useState(0);
  const [polling, setPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [pollError, setPollError] = useState(null);
  const [quotaReconcileState, setQuotaReconcileState] = useState("idle");

  const scopeToken = () => ({ ...scopeRef.current });
  const isCurrentScope = (token) =>
    mountedRef.current &&
    scopeRef.current.key === token.key &&
    scopeRef.current.generation === token.generation;
  const commitIfCurrent = (token, write) => {
    if (!isCurrentScope(token)) return false;
    write();
    return true;
  };
  const abortScopeRequests = () => {
    loadControllerRef.current?.abort();
    loadControllerRef.current = null;
    requestRef.current?.abort();
    requestRef.current = null;
    pollControllerRef.current?.abort();
    pollControllerRef.current = null;
    quotaReconcileControllerRef.current?.abort();
    quotaReconcileControllerRef.current = null;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      scopeRef.current = {
        ...scopeRef.current,
        generation: scopeRef.current.generation + 1,
      };
      abortScopeRequests();
    };
  }, []);

  useLayoutEffect(() => {
    const token = scopeToken();
    abortScopeRequests();
    idempotencyKeyRef.current = null;
    commitIfCurrent(token, () => {
      setLoading(true);
      setAccess(null);
      setQuota(null);
      setAnalysis(null);
      setHistory([]);
      setAnalyzing(false);
      setError(null);
      setErrorPhase(null);
      setReplayNotice(false);
      setPollCycle(0);
      setPolling(false);
      setPollTimedOut(false);
      setPollError(null);
      setQuotaReconcileState("idle");
    });
  }, [contextKey]);

  useEffect(() => {
    if (preserveScrollOnFocus) {
      titleRef.current?.focus({ preventScroll: true });
      return;
    }
    titleRef.current?.focus();
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    titleRef.current?.scrollIntoView?.({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [contextKey, preserveScrollOnFocus]);

  useEffect(() => {
    const controller = new AbortController();
    const token = scopeToken();
    loadControllerRef.current?.abort();
    loadControllerRef.current = controller;
    commitIfCurrent(token, () => {
      setLoading(true);
      setAccess(null);
      setQuota(null);
      setAnalysis(null);
      setHistory([]);
      setError(null);
      setErrorPhase(null);
      setReplayNotice(false);
      setPolling(false);
      setPollTimedOut(false);
      setPollError(null);
      setQuotaReconcileState("idle");
    });
    Promise.all([
      fetchAiAccess(workId, { signal: controller.signal }),
      fetchResourceGroupAiHistory(
        workId,
        { contextId },
        { signal: controller.signal },
      ),
    ])
      .then(([accessPayload, historyPayload]) => {
        const analyses = Array.isArray(historyPayload?.analyses)
          ? historyPayload.analyses
          : [];
        commitIfCurrent(token, () => {
          setAccess(accessPayload);
          setQuota(accessPayload.quota);
          setHistory(analyses);
          setAnalysis(analyses[0] ?? null);
        });
      })
      .catch((caught) => {
        if (caught?.code !== "request_cancelled") {
          commitIfCurrent(token, () => {
            setError(caught);
            setErrorPhase("load");
          });
        }
      })
      .finally(() => {
        commitIfCurrent(token, () => {
          if (loadControllerRef.current === controller) {
            loadControllerRef.current = null;
            setLoading(false);
          }
        });
      });
    return () => {
      controller.abort();
      if (loadControllerRef.current === controller) {
        loadControllerRef.current = null;
      }
    };
  }, [contextKey, reloadKey]);

  const exhausted = quota?.unlimited === false && quota?.remaining === 0;
  const processing =
    analysis?.state === "reserved" || analysis?.status === "processing";
  const baseCanAnalyze =
    access?.enabled === true
    && !exhausted
    && !analyzing
    && !processing
    && !presentationReadOnly;
  const quotaLabel = quotaText(quota, access?.plan?.period);
  const canRetryAnalysis =
    errorPhase === "analysis" &&
    (error?.retryable === true || RETRYABLE_ANALYSIS_CODES.has(error?.code));
  const analysisErrorBlocksPrimary =
    errorPhase === "analysis" && error && error.retryable !== true;
  const quotaReconcileBlocksAnalysis = new Set(["loading", "failed"]).has(
    quotaReconcileState,
  );
  const canAnalyze =
    baseCanAnalyze &&
    !analysisErrorBlocksPrimary &&
    !quotaReconcileBlocksAnalysis;
  const needsLowerLevelAdvice =
    errorPhase === "analysis" &&
    error?.code === "resource_group_evidence_invalid";

  const applyFetchedAnalysis = (token, nextAnalysis) => {
    if (!nextAnalysis || typeof nextAnalysis.analysisId !== "string") {
      commitIfCurrent(token, () => {
        setPollError({
          message: "Não foi possível interpretar o status desta análise.",
          retryable: false,
        });
      });
      return null;
    }
    const stillProcessing =
      nextAnalysis.state === "reserved" || nextAnalysis.status === "processing";
    commitIfCurrent(token, () => {
      setAnalysis(nextAnalysis);
      setHistory((current) => [
        nextAnalysis,
        ...current.filter((run) => run.analysisId !== nextAnalysis.analysisId),
      ]);
      setPollTimedOut(false);
      if (
        !stillProcessing &&
        nextAnalysis.state === "failed" &&
        nextAnalysis.status !== "provider_unavailable"
      ) {
        setPollError({
          message:
            "A análise não foi concluída. Esta tentativa não consumiu uma nova cota.",
          retryable: false,
        });
      } else {
        setPollError(null);
      }
    });
    return stillProcessing;
  };

  const reconcileTerminalAccess = async (token) => {
    if (!isCurrentScope(token)) return false;
    quotaReconcileControllerRef.current?.abort();
    const controller = new AbortController();
    quotaReconcileControllerRef.current = controller;
    commitIfCurrent(token, () => {
      setQuotaReconcileState("loading");
    });
    try {
      const nextAccess = await fetchAiAccess(workId, {
        signal: controller.signal,
      });
      if (!isCurrentScope(token)) return false;
      commitIfCurrent(token, () => {
        setAccess(nextAccess);
        setQuota(nextAccess.quota);
        setQuotaReconcileState("ready");
      });
      return true;
    } catch (caught) {
      if (caught?.code !== "request_cancelled") {
        commitIfCurrent(token, () => {
          setQuotaReconcileState("failed");
        });
      }
      return false;
    } finally {
      if (quotaReconcileControllerRef.current === controller) {
        quotaReconcileControllerRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!processing || !analysis?.analysisId) return undefined;
    const token = scopeToken();
    const { analysisId } = analysis;
    const controller = new AbortController();
    let cancelled = false;
    let delayIndex = 0;
    pollControllerRef.current?.abort();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollControllerRef.current = controller;
    pollTimerRef.current = null;

    const pausePolling = () => {
      commitIfCurrent(token, () => {
        setPolling(false);
        setPollTimedOut(true);
      });
      if (pollControllerRef.current === controller) {
        pollControllerRef.current = null;
      }
    };

    const scheduleNext = (tick) => {
      if (!isCurrentScope(token) || cancelled) return;
      if (delayIndex >= PROCESSING_POLL_DELAYS_MS.length) {
        pausePolling();
        return;
      }
      const delay = PROCESSING_POLL_DELAYS_MS[delayIndex];
      delayIndex += 1;
      pollTimerRef.current = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (!isCurrentScope(token) || cancelled || controller.signal.aborted)
        return;
      pollTimerRef.current = null;
      commitIfCurrent(token, () => setPolling(true));
      let continuePolling = false;
      try {
        const payload = await fetchAiAnalysis(workId, analysisId, {
          signal: controller.signal,
        });
        if (!isCurrentScope(token) || cancelled) return;
        const processingState = applyFetchedAnalysis(token, payload?.analysis);
        continuePolling = processingState === true;
        if (processingState === false) {
          await reconcileTerminalAccess(token);
        }
      } catch (caught) {
        if (
          !isCurrentScope(token) ||
          cancelled ||
          caught?.code === "request_cancelled"
        )
          return;
        if (caught?.retryable === false) {
          commitIfCurrent(token, () => setPollError(caught));
        } else {
          continuePolling = true;
        }
      } finally {
        commitIfCurrent(token, () => setPolling(false));
      }
      if (continuePolling) {
        scheduleNext(tick);
      } else if (pollControllerRef.current === controller) {
        pollControllerRef.current = null;
      }
    };

    tick();
    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (pollControllerRef.current === controller) {
        pollControllerRef.current = null;
      }
      pollTimerRef.current = null;
    };
  }, [contextKey, processing, analysis?.analysisId, pollCycle]);

  const refreshProcessingStatus = async () => {
    const token = scopeToken();
    const analysisId = analysis?.analysisId;
    if (!isCurrentScope(token) || !processing || !analysisId || polling) return;
    pollControllerRef.current?.abort();
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
    const controller = new AbortController();
    pollControllerRef.current = controller;
    commitIfCurrent(token, () => {
      setPolling(true);
      setPollTimedOut(false);
      setPollError(null);
    });
    try {
      const payload = await fetchAiAnalysis(workId, analysisId, {
        signal: controller.signal,
      });
      if (!isCurrentScope(token)) return;
      const stillProcessing = applyFetchedAnalysis(token, payload?.analysis);
      if (stillProcessing) {
        commitIfCurrent(token, () => setPollCycle((value) => value + 1));
      } else if (stillProcessing === false) {
        await reconcileTerminalAccess(token);
      }
    } catch (caught) {
      if (caught?.code !== "request_cancelled") {
        commitIfCurrent(token, () => setPollError(caught));
      }
    } finally {
      commitIfCurrent(token, () => {
        if (pollControllerRef.current === controller) {
          pollControllerRef.current = null;
          setPolling(false);
        }
      });
    }
  };

  const analyze = async ({ retry = false } = {}) => {
    const token = scopeToken();
    const permitted = retry ? baseCanAnalyze && canRetryAnalysis : canAnalyze;
    if (!isCurrentScope(token) || !permitted || requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = uuidv4();
    const previousAnalysisId = analysis?.analysisId;
    const previousRemaining = quota?.remaining;
    commitIfCurrent(token, () => {
      setAnalyzing(true);
      setError(null);
      setErrorPhase(null);
      setReplayNotice(false);
    });
    try {
      const result = await createResourceGroupAiAnalysis(
        workId,
        { contextId },
        {
          idempotencyKey: idempotencyKeyRef.current,
          signal: controller.signal,
        },
      );
      const replayed =
        previousAnalysisId &&
        previousAnalysisId === result.analysis?.analysisId &&
        previousRemaining === result.quota?.remaining;
      commitIfCurrent(token, () => {
        setAnalysis(result.analysis);
        setQuota(result.quota);
        setHistory((current) => [
          result.analysis,
          ...current.filter(
            (run) => run.analysisId !== result.analysis?.analysisId,
          ),
        ]);
        setReplayNotice(Boolean(replayed));
        idempotencyKeyRef.current = null;
      });
    } catch (caught) {
      if (caught?.code !== "request_cancelled") {
        commitIfCurrent(token, () => {
          setError(caught);
          setErrorPhase("analysis");
          if (caught?.status === 429 && quota)
            setQuota({ ...quota, remaining: 0 });
          if (caught?.code === "ai_access_disabled") {
            setAccess((current) =>
              current ? { ...current, enabled: false } : current,
            );
            setQuota(null);
          }
        });
      }
    } finally {
      commitIfCurrent(token, () => {
        if (requestRef.current === controller) {
          requestRef.current = null;
          setAnalyzing(false);
        }
      });
    }
  };

  return (
    <section
      className="item-ai-panel resource-group-ai-panel"
      aria-labelledby="resource-group-ai-title"
    >
      <header className="item-ai-header resource-group-ai-header">
        <div>
          <span className="item-ai-eyebrow">Ampli Obras com IA</span>
          <h3 id="resource-group-ai-title" ref={titleRef} tabIndex="-1">
            Entenda os desvios deste grupo
          </h3>
          <p>
            {groupName || "Grupo de insumos"} · WBS {contextId}
          </p>
        </div>
        {typeof onClose === "function" ? (
          <button type="button" className="item-ai-close" onClick={onClose}>
            Voltar aos insumos
          </button>
        ) : null}
      </header>
      <p className="resource-group-ai-scope">
        A análise percorre todos os insumos vinculados a este grupo, mesmo os
        que não aparecem na página atual.
      </p>

      {loading ? (
        <p className="item-ai-loading" role="status">
          Preparando o grupo e o histórico…
        </p>
      ) : null}
      {!loading && access ? (
        <>
          {access.enabled && presentationReadOnly ? (
            <p
              aria-label="Análise somente leitura"
              className="item-ai-provider-warning item-ai-provider-warning--demo"
              role="status"
            >
              <strong>Obra de apresentação: histórico somente leitura.</strong>
              <span>Novas análises estão desabilitadas e nenhuma cota será consumida.</span>
            </p>
          ) : access.enabled ? (
            <div className="resource-group-ai-action-zone">
              <div
                className={`item-ai-quota${
                  exhausted ? " item-ai-quota--exhausted" : ""
                }`}
              >
                <strong>{quotaLabel}</strong>
                <span>
                  Uma execução usa uma cota da obra; filtros e páginas não usam.
                </span>
              </div>
              <button
                type="button"
                className="resource-group-ai-action"
                disabled={!canAnalyze}
                onClick={() => analyze()}
              >
                Analisar grupo de insumos
              </button>
              {analyzing ? (
                <p className="item-ai-loading" role="status">
                  Calculando todos os drivers e preparando a leitura da IA…
                </p>
              ) : null}
              {exhausted ? (
                <p className="item-ai-upsell">
                  Plano {access.plan?.label || "atual"}
                  {quota?.resetAt
                    ? ` · renova em ${dateLabel(quota.resetAt)}`
                    : ""}
                  . Fale com a Ampli para ampliar o plano. Seu histórico
                  continua disponível.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="item-ai-disabled" role="status">
              <strong>IA não habilitada para esta obra</strong>
              <span>
                O acesso pode ser liberado no Backoffice sem alterar o Ampli
                Obras.
              </span>
            </p>
          )}
        </>
      ) : null}

      {replayNotice ? (
        <p className="resource-group-ai-replay" role="status">
          Esta análise já existia e foi recuperada sem novo consumo.
        </p>
      ) : null}
      {quotaReconcileState === "loading" ? (
        <p
          className="resource-group-ai-replay resource-group-ai-quota-sync"
          role="status"
        >
          Atualizando o acesso e a cota desta obra…
        </p>
      ) : null}
      {quotaReconcileState === "failed" ? (
        <p
          className="item-ai-provider-warning resource-group-ai-quota-sync"
          role="alert"
        >
          O resultado foi concluído, mas a cota não pôde ser atualizada. Para
          evitar novo consumo incorreto, abra novamente este grupo antes de
          analisar outra vez.
        </p>
      ) : null}
      {processing ? (
        <div className="resource-group-ai-processing">
          <p className="resource-group-ai-replay" role="status">
            Esta análise já está em processamento. Aguarde a conclusão antes de
            iniciar outra.
          </p>
          <button
            type="button"
            disabled={polling}
            onClick={refreshProcessingStatus}
          >
            {polling ? "Atualizando status…" : "Atualizar status da análise"}
          </button>
          {pollTimedOut ? (
            <small role="status">
              A consulta automática foi pausada. A análise pode continuar no
              servidor; atualize o status quando quiser, sem usar nova cota.
            </small>
          ) : null}
        </div>
      ) : null}
      {pollError ? (
        <div
          className="item-ai-error resource-group-ai-poll-error"
          role="alert"
        >
          <span>
            {pollError.message ||
              "Não foi possível consultar o status desta análise."}
          </span>
        </div>
      ) : null}
      {error ? (
        <div className="item-ai-error" role="alert">
          <span>
            {error.message || "Não foi possível concluir a análise do grupo."}
          </span>
          {error.detail ? (
            <small className="resource-group-ai-error-detail">
              Detalhe: {error.detail}
            </small>
          ) : null}
          {errorPhase === "load" ? (
            <small className="resource-group-ai-error-advice">
              Tente carregar o contexto novamente. Nenhuma análise será criada e sua cota não será consumida.
            </small>
          ) : null}
          {needsLowerLevelAdvice ? (
            <small className="resource-group-ai-error-advice">
              Se o problema persistir após a próxima atualização da obra, informe o suporte com o código WBS deste grupo.
            </small>
          ) : null}
          {errorPhase === "load" ? (
            <button
              type="button"
              onClick={() => setReloadKey((value) => value + 1)}
            >
              Tentar carregar novamente
            </button>
          ) : null}
          {canRetryAnalysis ? (
            <button type="button" onClick={() => analyze({ retry: true })}>
              Tentar análise novamente
            </button>
          ) : null}
        </div>
      ) : null}
      <GroupResult
        analysis={analysis}
        history={history}
        readOnly={presentationReadOnly}
        workId={workId}
      />
    </section>
  );
};

export default ResourceGroupAiPanel;
